// vim: ft=javascriptreact
import classnames from 'classnames'
import { useState } from 'react'
import {
  Card,
  Row,
  Col,
  Button,
  Form,
  Alert,
  Spinner,
} from 'react-bootstrap'
import { format, parseISO } from 'date-fns'
import ConfirmButton from '../ConfirmButton'
import Note from '../Note'

const BookingCard = ({
  booking,
  isActive = false,
  isAdmin = false,
  onUpdateBooking,
  titleFunc = (confirmed, _resident, title) => title ? title : (confirmed ? 'Confirmed' : 'Requested'),
}) => {
  const {
    id,
    date,
    confirmed,
    created_at: createdAt,
    resident,
    notes,
    official_use: officialUse,
    title,
  } = booking
  const bookingDate = format(parseISO(date), 'EEEE, LLLL do, yyyy')
  const requestedDate = format(parseISO(createdAt), 'LLLL do, yyyy')
  const { email, first_name: firstName, last_name: lastName } = resident
  const [note, setNote] = useState('')
  const [cancelMode, setCancelMode] = useState(false)
  const [showAddNote, setShowAddNote] = useState(false)
  const [saving, setSaving] = useState(false)

  const onAddNote = () => {
    setSaving(true)
    onUpdateBooking(id, { notes: note, action: 'add_note' }).then(() => {
      setSaving(false)
      setShowAddNote(false)
      setNote('')
    })
  }

  const onCancel = () => {
    setSaving(true)
    onUpdateBooking(id, { notes: note, action: 'cancel' }).then(() => {
      setSaving(false)
      setNote('')
    })
  }

  const actions = !isActive ? [] : [
    <Button
      key="cancel"
      variant="outline-danger" size="sm"
      onClick={() => setCancelMode(true) }
    >
      { cancelMode ? 'Really Cancel' : 'Cancel' }
    </Button>
  ]

  actions.push(
    <Button
      key="add-note"
      variant="outline-secondary" size="sm"
      onClick={() => setShowAddNote(!showAddNote)}
    >
      Add Note
    </Button>
  )

  if (!confirmed && isAdmin && isActive) {
    actions.push(
      <ConfirmButton
        key="confirm"
        variant="outline-primary"
        size="sm"
        label="Confirm Payment"
        loadingLabel="Confirming..."
        onClick={() => onUpdateBooking(id, { action: 'confirm' })}
      />
    )
  }

  return (
    <Card className={classnames('booking', { 'requested': !confirmed, 'over': !isActive, 'official-use': officialUse })}>
      <Card.Header>
        {titleFunc(confirmed, resident, title)}
      </Card.Header>
      <Card.Body>
        <Card.Title>{bookingDate}</Card.Title>
        <Card.Text as="div">
          <Row>
            <Col xs="2">
              <i className={classnames('fa', { 'fa-circle-check': confirmed, 'fa-circle-question': !confirmed })} />
            </Col>
            <Col>
              <div className="booking-details text-muted">
                {isAdmin && (
                  <>
                    <div className="booking-owner">
                      {firstName} {lastName}
                    </div>
                    <div className="booking-owner">
                      {email}
                    </div>
                  </>
                )}
                <div className="booking-date">
                  Requested on {requestedDate}
                </div>

                {notes && notes.length > 0 && (
                  <div className="booking-notes">
                    {notes.map((note) => (<Note id={note.id} note={note.note} createdAt={note.created_at} />))}
                  </div>
                )}

                <div className="booking-id">
                  {id}
                </div>

                {showAddNote && saving && (
                  <div className="add-booking-note mt-2 pt-2">
                    <Spinner animation="border" role="status" variant="primary" className="mb-2" />
                  </div>
                )}
                {showAddNote && !saving && (
                  <div className="add-booking-note mt-2 pt-2">
                    <Form.Group className="mb-3">
                      <Form.Label>Add note or comment</Form.Label>
                      <Form.Control as="textarea" placeholder="Note" rows={5} onChange={(e) => setNote(e.target.value)} />
                    </Form.Group>

                    <div className="d-flex justify-content-evenly">
                      <Button
                        key="cancel"
                        variant="outline-secondary" size="sm"
                        onClick={() => setShowAddNote(!showAddNote)}
                      >
                        Cancel
                      </Button>

                      <Button
                        key="add-note"
                        variant="outline-primary" size="sm"
                        onClick={onAddNote}
                      >
                        Add Note
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Card.Text>
      </Card.Body>

      {cancelMode && saving && (
        <Alert
          variant="warning"
        >
          <Spinner animation="border" role="status" variant="primary" className="mb-2" />
        </Alert>
      )}

      {cancelMode && !saving && (
        <Alert
          dismissible
          variant="warning"
          onClose={() => setCancelMode(false)}
        >
          <Form.Group className="mb-3">
            <Form.Label>Additional notes or comments?</Form.Label>
            <Form.Control as="textarea" placeholder="Notes" rows={5} onChange={(e) => setNote(e.target.value)} />
          </Form.Group>

          <div className="d-flex justify-content-evenly">
            <Button
              key="cancel"
              variant="danger" size="sm"
              onClick={onCancel}
            >
              Really Cancel
            </Button>
          </div>
        </Alert>
      )}

      {(!cancelMode && !showAddNote) && (
        <div className="mb-2 d-flex justify-content-evenly">
          {actions}
        </div>
      )}
    </Card>
  )

}

export default BookingCard
